import { useState, useEffect, useLayoutEffect } from 'react';

interface IAsyncCallState<T>
{
    loading: boolean;
    error?: Error;
    content?: T;
}

type PromiseFunction<T = any> = (...args: any[]) => Promise<T>;

export function useAsyncCall<T = any>(promiseFn: PromiseFunction<T>, ...parameters: any[]): IAsyncCallState<T>
{
    const [state, setState] = useState<IAsyncCallState<T>>({ loading: false });

    function sideEffect(): () => void
    {
        let cancelled = false;
        setState({ loading: true });

        promiseFn.apply(null, parameters)
            .then(x =>
            {
                if (cancelled)
                    return;

                setState({ content: x, loading: false, error: undefined });
            })
            .catch(e =>
            {
                if (cancelled)
                    return;

                setState({ error: e, loading: false, content: undefined });
            });

        return () => { cancelled = true; }
    }

    useEffect.apply(null, [sideEffect, [promiseFn].concat(parameters)]);

    return state;
}

export function useRecurrentAsyncCall<T = any>(interval: number, promiseFn: PromiseFunction<T>, ...parameters: any[]): IAsyncCallState<T>
{
    const [state, setState] = useState<IAsyncCallState<T>>({ loading: false });

    function sideEffect(): () => void
    {
        let cancelled = false;

        function asyncCall(previousContent?: T): void
        {
            if (cancelled)
                return;

            setState({ content: previousContent, loading: true });

            promiseFn.apply(null, parameters)
                .then(x =>
                {
                    if (cancelled)
                        return;

                    setState({ content: x, loading: false, error: undefined });
                    setTimeout(() => asyncCall(x), interval);
                })
                .catch(e =>
                {
                    if (cancelled)
                        return;

                    setState({ error: e, loading: false, content: undefined });
                });
        }

        asyncCall(undefined);
        return () => { cancelled = true; }
    }

    useLayoutEffect.apply(null, [sideEffect, [promiseFn].concat(parameters)]);

    return state;
}